export interface House {
  id: string;
  name: string;
  icon: string;
  points: number;
  color: string;
  accent: string;
  gemGradient: string;
  backgroundImage?: string;
  isArchived: boolean;
  deactivatedOn?: string | null;
}

export interface Transaction {
  id: string;
  houseId: string;
  pointsDelta: number;
  timestamp: Date;
  source: 'manual' | 'coupon';
}

export interface AppConfig {
  activeHouseCount: number;
}

export interface User {
  id: string;
  type: 'teacher' | 'mascot' | 'kiosk' | 'student';
  houseId?: string;
  password: string;
}

export const defaultHouses: House[] = [
  {
    id: 'house1',
    name: 'White House',
    icon: '❄️',
    points: 2465,
    color: '#E2E8F0', // Slate 200/Silver
    accent: '#94A3B8', // Slate 400
    gemGradient: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 50%, #94a3b8 100%)',
    backgroundImage: '/1.png',
    isArchived: false,
  },
  {
    id: 'house2',
    name: 'Yellow House',
    icon: '☀️',
    points: 2890,
    color: '#FACC15', // Yellow 400
    accent: '#CA8A04', // Yellow 600
    gemGradient: 'linear-gradient(180deg, #fef08a 0%, #facc15 50%, #ca8a04 100%)',
    backgroundImage: '/2.png',
    isArchived: false,
  },
  {
    id: 'house3',
    name: 'Red House',
    icon: '🔥',
    points: 2340,
    color: '#EF4444', // Red 500
    accent: '#991B1B', // Red 800
    gemGradient: 'linear-gradient(180deg, #fca5a5 0%, #ef4444 50%, #991b1b 100%)',
    backgroundImage: '/3.png',
    isArchived: false,
  },
  {
    id: 'house4',
    name: 'Blue House',
    icon: '💧',
    points: 2120,
    color: '#3B82F6', // Blue 500
    accent: '#1E40AF', // Blue 800
    gemGradient: 'linear-gradient(180deg, #93c5fd 0%, #3b82f6 50%, #1e40af 100%)',
    backgroundImage: '/4.png',
    isArchived: false,
  },
  {
    id: 'house5',
    name: 'Green House',
    icon: '🌿', // Plant
    points: 1200, // Reset/New points
    color: '#22C55E', // Green 500
    accent: '#15803D', // Green 700
    gemGradient: 'linear-gradient(180deg, #86efac 0%, #22c55e 50%, #15803d 100%)', // fixed gradient
    backgroundImage: '/1.png', // Fallback
    isArchived: false,
  },
];

export const defaultConfig: AppConfig = {
  activeHouseCount: 6,
};

// Development-only demo users for testing house kiosk functionality
// These should NEVER be used in production environments
const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.DEV;

export const defaultUsers: User[] = isDevelopment ? [
  { id: 'teacher', type: 'teacher', password: 'teacher123' },
  { id: 'mascot1', type: 'mascot', houseId: 'house1', password: 'house1' },
  { id: 'mascot2', type: 'mascot', houseId: 'house2', password: 'house2' },
  { id: 'mascot3', type: 'mascot', houseId: 'house3', password: 'house3' },
  { id: 'mascot4', type: 'mascot', houseId: 'house4', password: 'house4' },
  { id: 'mascot5', type: 'mascot', houseId: 'house5', password: 'house5' },
  { id: 'kiosk', type: 'kiosk', password: 'kiosk123' },
] : [];

// Legacy exports for backward compatibility
export const houses = defaultHouses;

export const getRankings = (houseList: House[] = defaultHouses) => {
  return [...houseList].filter(h => !h.isArchived).sort((a, b) => b.points - a.points);
};

export const addPoints = (houseId: string, points: number): House | undefined => {
  const house = defaultHouses.find(h => h.id === houseId);
  if (house) {
    house.points += points;
    return house;
  }
  return undefined;
};
